import { getZindexByType } from '~/utils/style-utils';

const getTemplateRows = ({ hasHeader, hasSubHeader, hasStaticHeader, hasAppBanner, hasSearchBar }) => {
	const rgh = hasStaticHeader ? `var(--regulationHeight)` : '0';
	const abh = hasAppBanner ? `var(--appbannerHeight)` : '0';
	const hdh = hasHeader ? `var(--headerHeight)` : '0';
	const shh = hasSubHeader ? `var(--subHeaderHeight)` : '0';
	const sch = hasSearchBar ? `var(--searchBarHeight)` : '0';
	return `${rgh} ${abh} ${hdh} ${shh} ${sch} auto auto auto`;
};

const getTemplateRowsSmaller = ({
	hasHeader,
	hasSmallerBrandingHeader,
	hasStaticHeader,
	hasAppBanner,
	hasSearchBar,
	hasNativeHeader,
	hasTopNavigation
}) => {
	// No subheader for smaller screens
	const rgh = hasStaticHeader ? `var(--regulationHeight)` : '0';
	const abh = hasAppBanner ? `var(--appbannerHeight)` : '0';
	const hdh = hasHeader && !hasNativeHeader && hasSmallerBrandingHeader ? `var(--smallerHeaderHeight)` : '0';
	// Don't allocate space for top navigation when native header is enabled
	const htn = hasTopNavigation && !hasNativeHeader ? `var(--topNavHeight)` : '0';
	const sch = hasSearchBar ? `var(--searchBarHeight)` : '0';
	return `${rgh} ${abh} ${hdh} ${htn} ${sch} 0 auto auto auto`;
};

const headerWrapperTop = (direction, smaller, hasRegulationsHeader) => {
	if (direction === 'bottom' && smaller) {
		return `transform: translateY(-100%);`;
	}
	return hasRegulationsHeader ? `top: var(--regulationHeight)` : `top: 0;`;
};

const sidebarHeight = ({ hasHeader, hasProductNav, hasStaticHeader }, smaller, useVH = false) => {
	const rgh = hasStaticHeader ? `var(--regulationHeight)` : '0px';
	const hdh = hasHeader ? `var(--headerHeight)` : '0px';
	const prh = hasProductNav ? `var(--subHeaderHeight)` : '0px';
	if (useVH) {
		const totalHeightVH = smaller ? `calc(100vh - ${rgh})` : `calc(100vh - (${rgh} + ${hdh} + ${prh}))`;
		return totalHeightVH;
	}
	const totalHeight = smaller ? `calc(100% - ${rgh})` : `calc(100% - (${rgh} + ${hdh} + ${prh}))`;
	return totalHeight;
};

const sidebarTop = ({
	hasHeader = false,
	hasProductNav = false,
	hasStaticHeader = false,
	ignoreSubheaderHeight = false
}) => {
	const rgh = hasStaticHeader ? `var(--regulationHeight)` : '0px';
	const hdh = hasHeader ? `var(--headerHeight)` : '0px';
	const prh = hasProductNav && !ignoreSubheaderHeight ? `var(--subHeaderHeight)` : '0px';
	const positionTop = `calc(${rgh} + ${hdh} + ${prh})`;
	return positionTop;
};

export const wrapperHeight = ({ hasProductNav, hasHeader }) => {
	const subHeaderHeight = hasProductNav ? 'var(--subHeaderHeight)' : '0px';
	const headerHeight = hasHeader ? 'var(--headerHeight)' : '0px';
	return `calc(100% - ${subHeaderHeight} - ${headerHeight})`;
};

export const wrapperMarginTop = ({ withinSidebar, hasProductNav, hasHeader, hasStaticHeader }) => {
	if (withinSidebar) {
		return '0px';
	}

	const subHeaderOffset = hasProductNav ? 'var(--subHeaderHeight)' : '0px';
	const headerOffset = hasHeader ? 'var(--headerHeight)' : '0px';
	const regulationHeaderOffset = hasStaticHeader ? 'var(--regulationHeight)' : '0px';

	return `calc(${subHeaderOffset} + ${headerOffset} + ${regulationHeaderOffset})`;
};

export const modalTop = ({
	setTopToZero,
	small,
	regOnly,
	hasStaticHeader,
	screenCenter,
	welcomePopup,
	notificationPopup,
	...rest
}) => {
	if (setTopToZero) return '0px';
	if (welcomePopup) {
		return 'auto';
	}
	if (notificationPopup) {
		if (small) {
			return `calc(${hasStaticHeader && 'var(--regulationHeight)'} + 10%)`;
		}
		return '20%';
	}
	if (regOnly) {
		if (hasStaticHeader) {
			return `var(--regulationHeight)`;
		}
		return '0px';
	}
	if (small) {
		if (hasStaticHeader) {
			return `var(--regulationHeight)`;
		}
		return '0px';
	}
	if (screenCenter) {
		return '50%';
	}
	return sidebarTop({ hasStaticHeader, ...rest });
};

export const modalLeft = ({ screenCenter, notificationPopup, welcomePopup, showSidebar, small }) => {
	if (notificationPopup) {
		if (small) {
			return 'calc(50%)';
		}
		if (showSidebar) {
			return 'calc(50% - 375px)';
		}
		return '50%';
	}
	if (welcomePopup) {
		if (small) {
			return '50%';
		}
		return 'calc(100% - 375px)';
	}
	if (screenCenter) {
		return '50%';
	}
	return '0px';
};

export const modalZIndex = ({ notificationPopup, blockingModalView, blockedView }) => {
	if (notificationPopup) {
		return getZindexByType('inbox-notification-slider');
	}
	if (blockingModalView) {
		return getZindexByType('blocking-modal-view');
	}
	if (blockedView) {
		return getZindexByType('blocked-view') - 1;
	}
	return getZindexByType('modal');
};

export const calcModalWidth = ({ withinSidebar, modalWidth, notificationPopup }) => {
	if (notificationPopup) {
		return `${modalWidth}px`;
	}
	if (withinSidebar) {
		return '375px';
	}
	if (modalWidth) {
		return `${modalWidth}px`;
	}
	return '100%';
};

/**
 * Calculates the height for a modal based on various conditions.
 *
 * @param {Object} [options] - Configuration options for the modal height calculation.
 * @param {boolean} [options.small] - Indicates if the modal is small.
 * @param {boolean} [options.hasStaticHeader] - Indicates if the modal has a static header.
 * @param {boolean} [options.withinSidebar] - Indicates if the modal is displayed within a sidebar.
 * @param {string} [options.customHeight] - A custom height value for the modal, if provided.
 * @param {boolean} [options.welcomePopup] - Indicates if the modal is a welcome popup.
 * @returns {string} - The calculated height for the modal (e.g., `auto`, `100%`, or a CSS calc expression).
 */
export const calcModalHeight = ({ small, hasStaticHeader, withinSidebar, customHeight, welcomePopup }) => {
	if (welcomePopup) {
		return 'auto';
	}
	if (small) {
		if (hasStaticHeader) {
			if (customHeight) {
				return `calc(${customHeight} - var(--regulationHeight))`;
			}
			return `calc(100% - var(--regulationHeight))`;
		}
		return '100%';
	}
	if (withinSidebar) {
		return 'auto';
	}
	return 'fit-content';
};

export const calcTransform = ({ notificationPopup, welcomePopup, screenCenter, showSidebar, small }) => {
	if (screenCenter) {
		return `translateX(-50%) translateY(-50%)`;
	}
	if (notificationPopup) {
		if (showSidebar) {
			return 'none';
		}
		return 'translateX(-50%)';
	}
	if (welcomePopup) {
		if (small) {
			return `translateX(-50%)`;
		}
		return 'none';
	}
	return 'none';
};

const sidebarSmallTop = ({ hasStaticHeader, hasHeader, hasAppBanner }) => {
	const rgh = hasStaticHeader ? `var(--regulationHeight)` : '0px';
	const abh = hasAppBanner ? `var(--appbannerHeight)` : '0px';
	const hdh = hasHeader ? `var(--smallerHeaderHeight)` : '0px';
	const prh = '0px';
	return `calc(${rgh} + ${hdh} + ${prh} + ${abh})`;
};

const overlaySmallerTop = ({ hasStaticHeader, hasAppBanner }) => {
	const sth = hasStaticHeader ? `var(--regulationHeight)` : '0px';
	const abh = hasAppBanner ? `var(--appbannerHeight)` : '0px';

	return `calc(${sth} + ${abh})`;
};

const spinnerFillerTop = ({ hasStaticHeader, hasSubHeader, hasHeader }) => {
	const h = hasHeader ? `var(--headerHeight)` : '0px';
	const sh = hasSubHeader ? `var(--subHeaderHeight)` : '0px';
	const sth = hasStaticHeader ? `var(--regulationHeight)` : '0px';

	return `calc(${h} + ${sh} + ${sth})`;
};

const fixedCasinoTop = ({ hasStaticHeader }) => {
	return hasStaticHeader
		? `calc(var(--regulationHeight) + var(--headerHeight) + var(--subHeaderHeight)
		  )`
		: `calc(var(--headerHeight) + var(--subHeaderHeight))`;
};

const mainWrapperMinHeight = ({
	hasHeader,
	hasSubHeader,
	hasStaticHeader,
	hasAppBanner,
	hasSearchBar,
	hasHeroHeader
}) => {
	const rgh = hasStaticHeader ? `var(--regulationHeight)` : '0px';
	const abh = hasAppBanner ? `var(--appbannerHeight)` : '0px';
	const hdh = hasHeader ? `var(--headerHeight)` : '0px';
	const shh = hasSubHeader ? `var(--subHeaderHeight)` : '0px';
	const sch = hasSearchBar ? `var(--searchBarHeight)` : '0px';
	const hhh = hasHeroHeader ? '350px' : '0px';
	return `calc(100vh - (${rgh} + ${abh} + ${hdh} + ${shh} + ${sch} + ${hhh}))`;
};

const mainWrapperMinHeightSmaller = ({
	hasHeader,
	hasSmallerBrandingHeader,
	hasSubHeader,
	hasStaticHeader,
	hasAppBanner,
	hasSearchBar,
	hasHeroHeader,
	hasTopNavigation,
	transparentModal
}) => {
	const rgh = hasStaticHeader ? `var(--regulationHeight)` : '0px';
	const abh = hasAppBanner ? `var(--appbannerHeight)` : '0px';
	const hdh = hasHeader && hasSmallerBrandingHeader ? `var(--smallerHeaderHeight)` : '0px';
	const shh = hasSubHeader ? `var(--subHeaderHeight)` : '0px';
	const sch = hasSearchBar ? `var(--searchBarHeight)` : '0px';
	const hhh = hasHeroHeader ? '264px' : '0px';
	const htn = hasTopNavigation ? `var(--topNavHeight)` : '0px';
	const tqd = transparentModal === 'mobile-quick-deposit' ? '1000px' : '0px';
	return `calc(100vh - (${tqd} + ${rgh} + ${abh} + ${hdh} + ${shh} + ${sch} + ${hhh} + ${htn}))`;
};

const iOSRE = /iPad|iPhone|iPod/;

const isIOS = () => iOSRE.test(navigator.userAgent);
const isIOSSafari = () => isIOS() && /WebKit/.test(navigator.userAgent) && !/CriOS/.test(navigator.userAgent);

const setTransformNone = id => {
	document.getElementById(id).classList.add('u-transform-none');
};

const removeTransformNone = id => {
	document.getElementById(id).classList.remove('u-transform-none');
};

export {
	getTemplateRows,
	getTemplateRowsSmaller,
	headerWrapperTop,
	sidebarTop,
	sidebarHeight,
	sidebarSmallTop,
	overlaySmallerTop,
	fixedCasinoTop,
	isIOS,
	iOSRE,
	spinnerFillerTop,
	setTransformNone,
	removeTransformNone,
	isIOSSafari,
	mainWrapperMinHeight,
	mainWrapperMinHeightSmaller
};
